import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import type { ReactNode } from 'react'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { ProtectedRoute, SuperAdminRoute } from '@/components/layout/guards.tsx'
import { AdminDesktopAppPage } from '@/pages/admin/desktop-app.tsx'
import { AdminReportsPage } from '@/pages/admin/reports.tsx'
import { AdminSettingsPage } from '@/pages/admin/settings.tsx'
import { AdminTransactionsPage } from '@/pages/admin/transactions.tsx'
import { AdminUsersPage } from '@/pages/admin/users.tsx'
import { DashboardPage } from '@/pages/dashboard.tsx'
import { LandingPage } from '@/pages/landing.tsx'
import { PrivacyPage } from '@/pages/legal/privacy.tsx'
import { TermsPage } from '@/pages/legal/terms.tsx'
import { ModelsPage } from '@/pages/models.tsx'
import { NotFoundPage } from '@/pages/not-found.tsx'
import { ProfilePage } from '@/pages/profile.tsx'
import { TransactionsPage } from '@/pages/transactions.tsx'
import { UsagePage } from '@/pages/usage.tsx'
import { VipSupportPage } from '@/pages/vip-support.tsx'
import { ApiRequestError } from '@/lib/api-client.ts'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      // An expired session is not worth retrying; everything else gets one retry.
      retry: (failureCount, error) =>
        error instanceof ApiRequestError && error.isUnauthenticated ? false : failureCount < 1,
    },
  },
})

function Protected({ children }: { children: ReactNode }) {
  return <ProtectedRoute>{children}</ProtectedRoute>
}

function AdminOnly({ children }: { children: ReactNode }) {
  return (
    <ProtectedRoute>
      <SuperAdminRoute>{children}</SuperAdminRoute>
    </ProtectedRoute>
  )
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />

          <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/models" element={<Protected><ModelsPage /></Protected>} />
          <Route path="/usage" element={<Protected><UsagePage /></Protected>} />
          <Route path="/transactions" element={<Protected><TransactionsPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/vip-support" element={<Protected><VipSupportPage /></Protected>} />

          <Route path="/admin" element={<Navigate to="/admin/reports" replace />} />
          <Route path="/admin/reports" element={<AdminOnly><AdminReportsPage /></AdminOnly>} />
          <Route path="/admin/transactions" element={<AdminOnly><AdminTransactionsPage /></AdminOnly>} />
          <Route path="/admin/desktop-app" element={<AdminOnly><AdminDesktopAppPage /></AdminOnly>} />
          <Route path="/admin/settings" element={<AdminOnly><AdminSettingsPage /></AdminOnly>} />
          <Route path="/admin/users" element={<AdminOnly><AdminUsersPage /></AdminOnly>} />

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </BrowserRouter>

      <Toaster
        position="top-center"
        toastOptions={{
          className:
            'rounded-lg! bg-surface-container! text-on-surface! shadow-level-3! border-none!',
        }}
      />
    </QueryClientProvider>
  )
}
